import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Card, CardContent, Typography, Rating, Chip,
    Skeleton, Alert, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { decryptId } from "../helper/idCodec.js";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import ArrowBackRoundedIcon      from "@mui/icons-material/ArrowBackRounded";
import LocalShippingRoundedIcon  from "@mui/icons-material/LocalShippingRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import ArticleRoundedIcon        from "@mui/icons-material/ArticleRounded";
import SettingsRoundedIcon       from "@mui/icons-material/SettingsRounded";
import CalendarMonthRoundedIcon  from "@mui/icons-material/CalendarMonthRounded";
import OpenInNewRoundedIcon      from "@mui/icons-material/OpenInNewRounded";
import CheckCircleRoundedIcon    from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon         from "@mui/icons-material/CancelRounded";
import StarRoundedIcon           from "@mui/icons-material/StarRounded";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B", surface: "#FFFFFF",
};

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtRs   = n => n != null && n !== "" ? `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";
const fmtNum  = n => n != null ? Number(n).toLocaleString("en-IN") : "—";

// ── Section card ──────────────────────────────────────────────────────────
const SCard = ({ children, sx = {} }) => (
    <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", ...sx }}>
        {children}
    </Card>
);

const SHead = ({ icon, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {React.cloneElement(icon, { sx: { fontSize: 15, color: T.teal } })}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</Typography>
    </Box>
);

// ── Info row ──────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, accent }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" } }}>
        <Typography sx={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: accent || T.text, textAlign: "right", maxWidth: "60%" }}>{value ?? "—"}</Typography>
    </Box>
);

// ── Boolean badge ─────────────────────────────────────────────────────────
const BoolBadge = ({ value, trueLabel = "Yes", falseLabel = "No" }) => {
    if (value === true  || value === "true")  return <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}><CheckCircleRoundedIcon sx={{ fontSize: 14, color: T.emerald }} /><Typography sx={{ fontSize: 12, fontWeight: 700, color: T.emerald }}>{trueLabel}</Typography></Box>;
    if (value === false || value === "false") return <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}><CancelRoundedIcon sx={{ fontSize: 14, color: T.muted }} /><Typography sx={{ fontSize: 12, fontWeight: 600, color: T.muted }}>{falseLabel}</Typography></Box>;
    return <Typography sx={{ fontSize: 12, color: T.muted }}>—</Typography>;
};

// ── Document link — opens preview dialog ─────────────────────────────────
// (onView is injected by the parent component)
const DocLink = ({ label, url, onView }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" } }}>
        <Typography sx={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>{label}</Typography>
        {url ? (
            <Box
                onClick={() => onView && onView(label, url)}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: T.teal, cursor: "pointer", fontSize: 12, fontWeight: 700, "&:hover": { textDecoration: "underline" } }}
            >
                <OpenInNewRoundedIcon sx={{ fontSize: 13 }} /> View
            </Box>
        ) : (
            <Typography sx={{ fontSize: 12, color: T.muted }}>Not uploaded</Typography>
        )}
    </Box>
);

// ── Main component ────────────────────────────────────────────────────────
export default function TruckDetails() {
    const { id }   = useParams();
    const truckId  = id ? decryptId(id) : "";
    const navigate = useNavigate();

    const [truck,      setTruck]      = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [error,      setError]      = useState(null);
    const [docViewer,  setDocViewer]  = useState({ open: false, label: "", url: "" });

    const fetchTruck = useCallback(async () => {
        if (!truckId) { setError("Truck ID not found."); setLoading(false); return; }
        setLoading(true); setError(null);
        try {
            const url = API_ENDPOINTS.getTruckDetailsById.replace(":truck_id", truckId);
            const res = await getData(url);
            if (res.statusCode !== 200) throw new Error(res.message || "Failed to retrieve truck.");
            const d = res.result;
            setTruck({
                id:                    d.truck_id,
                name:                  d.truck_name,
                model:                 d.truck_model,
                regNumber:             d.truck_no,
                chassisNo:             d.chasis_no,
                pic:                   d.truck_pic,
                rating:                d.average_rating || 0,
                ownerName:             d.owner_name,
                ownerId:               d.truck_owner,
                capacityKg:            d.capacity_inkgs,
                capacityM3:            d.capacity_inspace,
                ownedByCompany:        d.owned_by_transport_company,
                taggedToTrip:          d.tagged_to_trip,
                rcFile:                d.rc_file,
                licenseFile:           d.license_file,
                pollutionCert:         d.pollution_certificate_file,
                ownerPricePerKm:       d.owner_price_per_km,
                ownerPricePerKg:       d.owner_price_per_kg,
                ownerMarkupPct:        d.owner_markup_percent,
                ownerMinFare:          d.owner_min_fare,
                demandPricingEnabled:  d.demand_pricing_enabled,
                createdAt:             d.createdAt,
                updatedAt:             d.updatedAt,
            });
        } catch (e) { setError(e.message || "Failed to fetch truck."); }
        finally     { setLoading(false); }
    }, [truckId]);

    useEffect(() => { fetchTruck(); }, [fetchTruck]);

    const status = truck?.taggedToTrip ? "On-Trip" : "Active";
    const statusCfg = truck?.taggedToTrip
        ? { bg: T.indigoLt, color: T.indigo }
        : { bg: T.emeraldLt, color: T.emerald };

    if (loading) return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", p: 3 }}>
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: "20px", mb: 3 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2 }}>
                {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={200} sx={{ borderRadius: "16px" }} />)}
            </Box>
        </Box>
    );

    if (error) return <Alert severity="error" sx={{ m: 3, borderRadius: "12px" }}>{error}</Alert>;
    if (!truck) return <Typography p={3} color="text.secondary">Truck not found.</Typography>;

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <IconButton onClick={() => navigate(-1)}
                        sx={{ bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "10px", color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                        <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Trucks</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap" }}>
                    {/* Truck thumbnail */}
                    <Box sx={{ width: 72, height: 56, borderRadius: "12px", overflow: "hidden", border: "2px solid rgba(255,255,255,0.3)", flexShrink: 0, bgcolor: "rgba(255,255,255,0.15)" }}>
                        <img
                            src={truck.pic && truck.pic.trim() ? truck.pic : "/images/Default-Truck.png"}
                            alt={truck.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.6 }}>
                            <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                {truck.name}
                            </Typography>
                            <Chip label={status} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, borderRadius: "7px", bgcolor: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.color}33` }} />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", px: 1.2, py: 0.3 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "monospace" }}>{truck.regNumber}</Typography>
                            </Box>
                            {truck.model && <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{truck.model}</Typography>}
                            {/* Rating inline */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <StarRoundedIcon sx={{ fontSize: 14, color: "#FFD700" }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
                                    {truck.rating > 0 ? Number(truck.rating).toFixed(1) : "No ratings"}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>Registered {fmtDate(truck.createdAt)}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Content grid ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1.2fr 1fr 1fr" }, gap: 2, mb: 2 }}>

                {/* Truck photo (full size) */}
                <SCard sx={{ gridRow: { lg: "1 / 3" }, overflow: "hidden" }}>
                    <Box sx={{ width: "100%", paddingTop: "62%", position: "relative", bgcolor: T.bg }}>
                        <img
                            src={truck.pic && truck.pic.trim() ? truck.pic : "/images/Default-Truck.png"}
                            alt={truck.name}
                            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    </Box>
                    {/* Rating below image */}
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5 }}>
                            <Rating value={truck.rating} precision={0.5} readOnly
                                sx={{ "& .MuiRating-iconFilled": { color: T.amber }, "& .MuiRating-iconEmpty": { color: T.border } }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                                {truck.rating > 0 ? `${Number(truck.rating).toFixed(1)} / 5` : "Not rated"}
                            </Typography>
                        </Box>
                    </CardContent>
                </SCard>

                {/* Specifications */}
                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<LocalShippingRoundedIcon />} title="Specifications" />
                        <InfoRow label="Truck Name"       value={truck.name} />
                        <InfoRow label="Model"            value={truck.model || "—"} />
                        <InfoRow label="Reg. Number"      value={truck.regNumber} />
                        {truck.chassisNo && <InfoRow label="Chassis No." value={truck.chassisNo} />}
                        <InfoRow label="Capacity"         value={`${fmtNum(truck.capacityKg)} kg · ${truck.capacityM3 != null ? `${Number(truck.capacityM3).toFixed(2)} m³` : "—"}`} />
                        <InfoRow label="Owned by Company" value={<BoolBadge value={truck.ownedByCompany} />} />
                        <InfoRow label="Current Status"   value={<Chip label={status} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, borderRadius: "6px", bgcolor: statusCfg.bg, color: statusCfg.color }} />} />
                    </CardContent>
                </SCard>

                {/* Owner info */}
                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<ManageAccountsRoundedIcon />} title="Owner" />
                        <InfoRow label="Owner Name"       value={truck.ownerName || "Unknown"} accent={T.teal} />
                        <InfoRow label="Price / km"       value={truck.ownerPricePerKm > 0 ? `${fmtRs(truck.ownerPricePerKm)} / km` : "Platform tier"} />
                        <InfoRow label="Price / kg"       value={truck.ownerPricePerKg > 0 ? `${fmtRs(truck.ownerPricePerKg)} / kg` : "Platform tier"} />
                        <InfoRow label="Markup %"         value={truck.ownerMarkupPct > 0 ? `${Number(truck.ownerMarkupPct).toFixed(1)}%` : "—"} />
                        <InfoRow label="Min Fare"         value={truck.ownerMinFare > 0 ? fmtRs(truck.ownerMinFare) : "—"} />
                    </CardContent>
                </SCard>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>

                {/* Pricing config */}
                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<SettingsRoundedIcon />} title="Pricing Configuration" />
                        <InfoRow label="Pricing Source" value={
                            (truck.ownerPricePerKm > 0 || truck.ownerPricePerKg > 0)
                                ? <Chip label="Owner-set Rate" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, borderRadius: "6px", bgcolor: T.orangeLt, color: T.orange }} />
                                : <Chip label="Platform Tier" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, borderRadius: "6px", bgcolor: T.tealLt, color: T.teal }} />
                        } />
                        <InfoRow label="Demand Pricing" value={<BoolBadge value={truck.demandPricingEnabled} trueLabel="Enabled" falseLabel="Disabled" />} />
                        <InfoRow label="Owner / km"    value={truck.ownerPricePerKm > 0 ? `${fmtRs(truck.ownerPricePerKm)}` : "Not set"} />
                        <InfoRow label="Owner / kg"    value={truck.ownerPricePerKg > 0 ? `${fmtRs(truck.ownerPricePerKg)}` : "Not set"} />
                        <InfoRow label="Markup"        value={truck.ownerMarkupPct > 0 ? `${Number(truck.ownerMarkupPct).toFixed(1)}%` : "Not set"} />
                        <InfoRow label="Minimum Fare"  value={truck.ownerMinFare > 0 ? fmtRs(truck.ownerMinFare) : "Not set"} />
                    </CardContent>
                </SCard>

                {/* Documents + meta */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <SCard>
                        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                            <SHead icon={<ArticleRoundedIcon />} title="Documents" />
                            <DocLink label="Registration Certificate (RC)" url={truck.rcFile}       onView={(l,u) => setDocViewer({ open: true, label: l, url: u })} />
                            <DocLink label="Pollution Certificate"          url={truck.pollutionCert}onView={(l,u) => setDocViewer({ open: true, label: l, url: u })} />
                            <DocLink label="License / Permit File"          url={truck.licenseFile}  onView={(l,u) => setDocViewer({ open: true, label: l, url: u })} />
                        </CardContent>
                    </SCard>

                    <SCard>
                        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                            <SHead icon={<CalendarMonthRoundedIcon />} title="Timeline" />
                            <InfoRow label="Registered on" value={fmtDate(truck.createdAt)} />
                            <InfoRow label="Last updated"  value={fmtDate(truck.updatedAt)} />
                        </CardContent>
                    </SCard>
                </Box>
            </Box>

            {/* ── Document viewer dialog ── */}
            {(() => {
                // Detect file type from URL to choose the right renderer
                const url  = docViewer.url || "";
                const ext  = (url.split("?")[0].split(".").pop() || "").toLowerCase();
                const isImg = ["jpg","jpeg","png","gif","webp","bmp","svg"].includes(ext);
                // For PDFs/docs: proxy through Google Docs Viewer to bypass
                // Content-Disposition:attachment set on Azure Blob Storage URLs.
                const viewerSrc = isImg ? url : `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;

                return (
                    <Dialog
                        open={docViewer.open}
                        onClose={() => setDocViewer({ open: false, label: "", url: "" })}
                        maxWidth="lg" fullWidth
                        PaperProps={{ sx: { borderRadius: "20px", height: "90vh" } }}
                    >
                        <DialogTitle sx={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            fontWeight: 800, fontSize: 16, fontFamily: "'Plus Jakarta Sans', sans-serif",
                            borderBottom: `1px solid ${T.border}`, pb: 2,
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <ArticleRoundedIcon sx={{ fontSize: 15, color: T.teal }} />
                                </Box>
                                {docViewer.label}
                            </Box>
                            <Tooltip title="Open in new tab">
                                <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer"
                                    sx={{ color: T.teal, bgcolor: T.tealLt, borderRadius: "8px" }}>
                                    <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </DialogTitle>

                        <DialogContent sx={{ p: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#1a1a2e" }}>
                            {url && (
                                isImg ? (
                                    // Images: render directly — no Content-Disposition issue
                                    <Box component="img"
                                        src={url} alt={docViewer.label}
                                        sx={{ maxWidth: "100%", maxHeight: "calc(90vh - 120px)", objectFit: "contain" }}
                                    />
                                ) : (
                                    // PDFs / other docs: Google Docs Viewer bypasses
                                    // Azure's Content-Disposition:attachment header
                                    <iframe
                                        key={url}
                                        src={viewerSrc}
                                        title={docViewer.label}
                                        style={{ width: "100%", height: "calc(90vh - 120px)", border: "none" }}
                                    />
                                )
                            )}
                        </DialogContent>

                        <DialogActions sx={{ borderTop: `1px solid ${T.border}`, px: 3, py: 1.5, gap: 1 }}>
                            <Button onClick={() => setDocViewer({ open: false, label: "", url: "" })}
                                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
                                Close
                            </Button>
                            <Button variant="contained" disableElevation
                                component="a" href={url} target="_blank" rel="noopener noreferrer"
                                startIcon={<OpenInNewRoundedIcon />}
                                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: T.teal, "&:hover": { bgcolor: T.tealDk } }}>
                                Open in new tab
                            </Button>
                        </DialogActions>
                    </Dialog>
                );
            })()}
        </Box>
    );
}
